/**
 * Copy text to the clipboard with fallbacks for embedded / iframe contexts
 * where navigator.clipboard is unavailable or blocked in production.
 */
export function copyTextToClipboard(text) {
  const str = String(text ?? '');
  if (!str) return Promise.resolve(false);

  const execCopy = () => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = str;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, str.length);
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);
      return copied;
    } catch {
      return false;
    }
  };

  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard.writeText(str).catch(() => {
      if (execCopy()) return true;
      throw new Error('Copy failed');
    });
  }

  if (execCopy()) return Promise.resolve(true);
  return Promise.reject(new Error('Copy failed'));
}

/**
 * Copy text that is visible on screen inside a dialog.
 *
 * The app runs in an iframe, where the async Clipboard API is often refused,
 * and inside an MUI Dialog the focus trap steals focus from the temporary
 * textarea `copyTextToClipboard` falls back to — so the copy grabs nothing.
 * Selecting the rendered node itself survives the trap. When both fail the
 * text is left selected so the user can press Ctrl/⌘+C.
 *
 * @param {string} text
 * @param {HTMLElement|null} node the element showing `text`
 * @returns {Promise<boolean>} whether the copy went through
 */
export async function copyFromVisibleNode(text, node) {
  const selectNode = () => {
    if (!node || typeof window === 'undefined' || !window.getSelection) return false;
    try {
      const range = document.createRange();
      range.selectNodeContents(node);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch {
      return false;
    }
  };

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(String(text ?? ''));
      return true;
    } catch {
      /* fall through */
    }
  }

  if (selectNode()) {
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    if (ok) {
      try {
        window.getSelection()?.removeAllRanges();
      } catch {
        /* no-op */
      }
      return true;
    }
  }

  selectNode();
  return false;
}
